import Button from 'devextreme-react/button';
import Popup from 'devextreme-react/popup';
import SelectBox from 'devextreme-react/select-box';
import TagBox from 'devextreme-react/tag-box';
import TextArea from 'devextreme-react/text-area';
import TextBox from 'devextreme-react/text-box';
import type { ToolbarItem } from 'devextreme/ui/popup';
import { useEffect, useMemo, useRef, useState } from 'react';
import { showConfirmDialog, showErrorToast, showSuccessToast, useShellLayout } from '@jarvis/platform-ui';
import {
  clearMediaReaction,
  createAdminCategory,
  createMediaTag,
  fetchAdminCategories,
  fetchAdminTags,
  fetchMediaAsset,
  fetchMediaTags,
  deletePrivateMedia,
  recordMediaUsage,
  replaceMediaAsset,
  setMediaReaction,
  setMediaRating,
  softDeleteMediaAsset,
  updateMediaAsset,
  updatePrivateMedia,
  type MediaAssetDetail,
  type MediaCategoryAdmin,
  type MediaTagAdmin
} from '../../../api/mediaLibrary';
import { writeImageToClipboard } from './mediaClipboard';
import { MediaLibraryReactionPicker } from './MediaLibraryReactionPicker';
import { useMediaLibraryAssetPopupSize } from './useMediaLibraryAssetPopupSize';

interface MediaLibraryAssetPopupProps {
  assetId: string | null;
  open: boolean;
  canManage: boolean;
  onClose: () => void;
  onChanged: () => void;
}

export function MediaLibraryAssetPopup({
  assetId,
  open,
  canManage,
  onClose,
  onChanged
}: MediaLibraryAssetPopupProps) {
  const onCloseRef = useRef(onClose);
  const onChangedRef = useRef(onChanged);
  onCloseRef.current = onClose;
  onChangedRef.current = onChanged;

  const { isNarrow } = useShellLayout();
  const { size, isMaximized, maximize, restoreDefault, onResizeEnd, maxWidth, maxHeight } =
    useMediaLibraryAssetPopupSize();

  const [detail, setDetail] = useState<MediaAssetDetail | null>(null);
  const [categories, setCategories] = useState<MediaCategoryAdmin[]>([]);
  const [tags, setTags] = useState<MediaTagAdmin[]>([]);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [categoryId, setCategoryId] = useState<string | null>(null);
  const [tagIds, setTagIds] = useState<string[]>([]);
  const [sourceUrl, setSourceUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const replaceInputRef = useRef<HTMLInputElement | null>(null);

  const activeCategories = useMemo(
    () => categories.filter((c) => c.isActive).map((c) => ({ id: c.id, name: c.name })),
    [categories]
  );
  const activeTags = useMemo(
    () => tags.filter((t) => t.isActive).map((t) => ({ id: t.id, name: t.name })),
    [tags]
  );

  const toolbarItems = useMemo((): ToolbarItem[] => {
    if (isNarrow) return [];
    return [
      {
        widget: 'dxButton',
        toolbar: 'top',
        location: 'after',
        options: {
          icon: 'revert',
          hint: 'Restore default size',
          stylingMode: 'text',
          elementAttr: { class: 'media-lib-detail-popup__title-btn' },
          onClick: () => restoreDefault()
        }
      },
      {
        widget: 'dxButton',
        toolbar: 'top',
        location: 'after',
        options: {
          icon: 'fullscreen',
          hint: isMaximized ? 'Already maximized' : 'Maximize',
          stylingMode: 'text',
          disabled: isMaximized,
          elementAttr: { class: 'media-lib-detail-popup__title-btn' },
          onClick: () => maximize()
        }
      }
    ];
  }, [isMaximized, isNarrow, maximize, restoreDefault]);

  useEffect(() => {
    if (!open || !assetId) {
      setDetail(null);
      setEditing(false);
      setLoading(false);
      return;
    }

    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const row = await fetchMediaAsset(assetId!);
        if (cancelled) return;
        setDetail(row);
        setName(row.name);
        setDescription(row.description ?? '');
        setCategoryId(row.categoryId);
        setTagIds(row.tagIds);
        setSourceUrl(row.sourceUrl ?? '');
        setEditing(false);
        if (row.kind === 'UserUpload' && row.canManage) {
          const tagRows = await fetchMediaTags('uploads');
          if (!cancelled) {
            setCategories([]);
            setTags(tagRows.map((tag) => ({ ...tag, isActive: true, usageCount: 0 })));
          }
        } else if (canManage && row.canManage) {
          const [cats, tagRows] = await Promise.all([fetchAdminCategories(), fetchAdminTags()]);
          if (!cancelled) {
            setCategories(cats);
            setTags(tagRows);
          }
        }
        void recordMediaUsage(assetId!, 'openFullView').catch(() => undefined);
      } catch (err) {
        if (!cancelled) {
          showErrorToast(err instanceof Error ? err.message : 'Could not load asset.');
          onCloseRef.current();
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [open, assetId, canManage]);

  async function onSave() {
    if (!assetId || !detail || busy) return;
    setBusy(true);
    try {
      const updated =
        detail.kind === 'UserUpload'
          ? await updatePrivateMedia(assetId, { name, description, sourceUrl, tagIds })
          : await updateMediaAsset(assetId, {
              name,
              description,
              categoryId,
              tagIds,
              sourceUrl
            });
      setDetail(updated);
      setEditing(false);
      showSuccessToast('Metadata saved.');
      onChangedRef.current();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not save metadata.');
    } finally {
      setBusy(false);
    }
  }

  async function onDelete() {
    if (!assetId || !detail || busy) return;
    const confirmed = await showConfirmDialog({
      title: 'Delete media',
      message: `Move “${detail.name}” to the recycle bin? It will leave the gallery immediately.`,
      confirmText: 'Delete',
      danger: true
    });
    if (!confirmed) return;
    setBusy(true);
    try {
      if (detail.kind === 'UserUpload') await deletePrivateMedia(assetId);
      else await softDeleteMediaAsset(assetId);
      showSuccessToast('Asset removed from gallery.');
      onChangedRef.current();
      onCloseRef.current();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not delete asset.');
    } finally {
      setBusy(false);
    }
  }

  async function onRate(stars: number) {
    if (!assetId || busy) return;
    setBusy(true);
    try {
      const updated = await setMediaRating(assetId, stars);
      setDetail(updated);
      onChangedRef.current();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not save rating.');
    } finally {
      setBusy(false);
    }
  }

  async function onReact(emoji: string) {
    if (!assetId || busy) return;
    setBusy(true);
    try {
      const updated = await setMediaReaction(assetId, emoji);
      setDetail(updated);
      onChangedRef.current();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not save reaction.');
    } finally {
      setBusy(false);
    }
  }

  async function onClearReaction() {
    if (!assetId || busy) return;
    setBusy(true);
    try {
      const updated = await clearMediaReaction(assetId);
      setDetail(updated);
      onChangedRef.current();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not clear reaction.');
    } finally {
      setBusy(false);
    }
  }

  async function onCopyImage() {
    if (!detail) return;
    try {
      const res = await fetch(detail.originalUrl, { credentials: 'include' });
      if (!res.ok) throw new Error('Could not fetch image.');
      const blob = await res.blob();
      await writeImageToClipboard(blob);
      await recordMediaUsage(detail.id, 'copyImage');
      showSuccessToast(
        detail.isAnimated
          ? 'GIF copied with a PNG fallback. Paste into Teams to test animation.'
          : 'Image copied.'
      );
      onChangedRef.current();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not copy image.');
    }
  }

  async function onDownload() {
    if (!detail) return;
    try {
      const res = await fetch(detail.originalUrl, { credentials: 'include' });
      if (!res.ok) throw new Error('Could not download image.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = detail.originalFileName || `${detail.name}${detail.extension}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      await recordMediaUsage(detail.id, 'download');
      showSuccessToast('Download started.');
      onChangedRef.current();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not download.');
    }
  }

  async function onCopyLink() {
    if (!detail) return;
    try {
      const link = `${window.location.origin}/apps/media-library?asset=${detail.id}`;
      await navigator.clipboard.writeText(link);
      await recordMediaUsage(detail.id, 'copyLink');
      showSuccessToast('Link copied.');
      onChangedRef.current();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not copy link.');
    }
  }

  async function onReplace(file: File | null) {
    if (!assetId || !file || busy) return;
    setBusy(true);
    try {
      const updated = await replaceMediaAsset(assetId, file);
      setDetail(updated);
      showSuccessToast('File replaced.');
      onChangedRef.current();
    } catch (err) {
      showErrorToast(err instanceof Error ? err.message : 'Could not replace file.');
    } finally {
      setBusy(false);
    }
  }

  const previewSrc = detail?.isAnimated
    ? detail.originalUrl
    : (detail?.posterUrl ?? detail?.thumbnailUrl ?? detail?.originalUrl);
  const userRating = detail?.currentUserRating ?? 0;

  return (
    <Popup
      visible={open}
      onHiding={onClose}
      title={detail?.name ?? 'Media details'}
      showCloseButton
      hideOnOutsideClick={!busy}
      toolbarItems={toolbarItems}
      width={isNarrow ? '100vw' : size.w}
      height={isNarrow ? '100dvh' : size.h}
      maxWidth={isNarrow ? '100vw' : maxWidth}
      maxHeight={isNarrow ? '100dvh' : maxHeight}
      fullScreen={isNarrow}
      resizeEnabled={!isNarrow && !isMaximized}
      dragEnabled={!isNarrow && !isMaximized}
      onResizeEnd={onResizeEnd}
      wrapperAttr={{ class: `media-lib-detail-popup-wrapper${isNarrow ? ' media-lib-detail-popup-wrapper--compact' : ''}` }}
    >
      <div className="media-lib-detail">
        {loading && !detail ? (
          <p className="utility-app__muted">Loading…</p>
        ) : !detail ? (
          <p className="utility-app__muted">No asset selected.</p>
        ) : (
          <>
            <div className="media-lib-detail__main">
              <div className="media-lib-detail__preview">
                {previewSrc ? (
                  <img src={previewSrc} alt={detail.name} />
                ) : (
                  <div className="media-lib__card-placeholder">No preview</div>
                )}
              </div>

              <div className="media-lib-detail__side">
                <div className="media-lib-detail__quick" role="group" aria-label="Quick actions">
                  <Button
                    text="Copy image"
                    stylingMode="outlined"
                    disabled={busy}
                    onClick={() => void onCopyImage()}
                  />
                  <Button
                    text="Download"
                    stylingMode="outlined"
                    disabled={busy}
                    onClick={() => void onDownload()}
                  />
                  <Button
                    text="Copy link"
                    stylingMode="outlined"
                    disabled={busy}
                    onClick={() => void onCopyLink()}
                  />
                </div>

                {editing && detail.canManage ? (
                  <div className="media-lib-detail__fields">
                    <label className="utility-app__label" htmlFor="media-lib-edit-name">
                      Name
                    </label>
                    <TextBox
                      inputAttr={{ id: 'media-lib-edit-name' }}
                      value={name}
                      width="100%"
                      onValueChanged={(e) => setName(String(e.value ?? ''))}
                    />
                    <label className="utility-app__label" htmlFor="media-lib-edit-desc">
                      Description
                    </label>
                    <TextArea
                      inputAttr={{ id: 'media-lib-edit-desc' }}
                      value={description}
                      height={80}
                      width="100%"
                      onValueChanged={(e) => setDescription(String(e.value ?? ''))}
                    />
                    {detail.kind !== 'UserUpload' ? (
                      <>
                        <label className="utility-app__label">Category</label>
                        <SelectBox
                          dataSource={activeCategories}
                          displayExpr="name"
                          valueExpr="id"
                          value={categoryId}
                          width="100%"
                          acceptCustomValue
                          searchEnabled
                          placeholder="Select or type a new category…"
                          onCustomItemCreating={(e) => {
                            const categoryName = String(e.text ?? '').trim();
                            if (!categoryName) {
                              e.customItem = null;
                              return;
                            }

                            const existing = activeCategories.find(
                              (category) =>
                                category.name.localeCompare(categoryName, undefined, {
                                  sensitivity: 'base'
                                }) === 0
                            );
                            if (existing) {
                              e.customItem = existing;
                              return;
                            }

                            e.customItem = createAdminCategory({ name: categoryName })
                              .then((created) => {
                                setCategories((current) =>
                                  current.some((category) => category.id === created.id)
                                    ? current
                                    : [...current, created].sort(
                                        (a, b) =>
                                          a.sortOrder - b.sortOrder ||
                                          a.name.localeCompare(b.name)
                                      )
                                );
                                return { id: created.id, name: created.name };
                              })
                              .catch((err) => {
                                showErrorToast(
                                  err instanceof Error
                                    ? err.message
                                    : 'Could not create category.'
                                );
                                return null;
                              });
                          }}
                          onValueChanged={(e) => setCategoryId((e.value as string | null) ?? null)}
                        />
                      </>
                    ) : null}
                    <label className="utility-app__label">Tags</label>
                    <TagBox
                      dataSource={activeTags}
                      displayExpr="name"
                      valueExpr="id"
                      value={tagIds}
                      width="100%"
                      acceptCustomValue
                      searchEnabled
                      showSelectionControls
                      applyValueMode="useButtons"
                      placeholder="Select or type a new tag…"
                      onCustomItemCreating={(e) => {
                        const tagName = String(e.text ?? '').trim();
                        if (!tagName) {
                          e.customItem = null;
                          return;
                        }

                        const existing = activeTags.find(
                          (tag) =>
                            tag.name.localeCompare(tagName, undefined, {
                              sensitivity: 'base'
                            }) === 0
                        );
                        if (existing) {
                          e.customItem = existing;
                          return;
                        }

                        e.customItem = createMediaTag(tagName)
                          .then((created) => {
                            const row = { ...created, isActive: true, usageCount: 0 };
                            setTags((current) =>
                              current.some((tag) => tag.id === row.id)
                                ? current
                                : [...current, row].sort((a, b) => a.name.localeCompare(b.name))
                            );
                            return row;
                          })
                          .catch((err) => {
                            showErrorToast(
                              err instanceof Error ? err.message : 'Could not create tag.'
                            );
                            return null;
                          });
                      }}
                      onValueChanged={(e) => setTagIds((e.value as string[]) ?? [])}
                    />
                    <label className="utility-app__label" htmlFor="media-lib-edit-source">
                      Source / attribution URL
                    </label>
                    <TextBox
                      inputAttr={{ id: 'media-lib-edit-source' }}
                      value={sourceUrl}
                      width="100%"
                      onValueChanged={(e) => setSourceUrl(String(e.value ?? ''))}
                    />
                  </div>
                ) : (
                  <div className="media-lib-detail__meta">
                    <p>
                      <strong>{detail.name}</strong>
                    </p>
                    {detail.description ? (
                      <p className="utility-app__muted">{detail.description}</p>
                    ) : null}
                    <p className="utility-app__muted">
                      {detail.categoryName ?? 'Uncategorized'}
                      {detail.tags.length ? ` · ${detail.tags.join(', ')}` : ''}
                    </p>
                    <p className="utility-app__muted media-lib-detail__tech">
                      {detail.width}×{detail.height}
                      {detail.isAnimated ? ` · ${detail.frameCount} frames` : ''}
                      {detail.animationDurationMs != null
                        ? ` · ${(detail.animationDurationMs / 1000).toFixed(1)}s`
                        : ''}
                      {` · ${Math.round(detail.fileSizeBytes / 1024)} KB`}
                      {` · ${detail.contentType}`}
                      {` · ${detail.originalFileName}`}
                    </p>
                    <section className="media-lib-detail__usage" aria-labelledby="media-lib-usage-heading">
                      <div className="media-lib-detail__section-head">
                        <span id="media-lib-usage-heading" className="utility-app__label">
                          Usage
                        </span>
                        {detail.lastUsedUtc ? (
                          <span className="utility-app__muted media-lib-detail__last-used">
                            Last used {new Date(detail.lastUsedUtc).toLocaleString()}
                          </span>
                        ) : null}
                      </div>
                      <div className="media-lib-detail__usage-grid">
                        <div className="media-lib-detail__stat media-lib-detail__stat--total">
                          <strong>{detail.totalUseCount}</strong>
                          <span>Total</span>
                        </div>
                        <div className="media-lib-detail__stat">
                          <strong>{detail.viewCount}</strong>
                          <span>Views</span>
                        </div>
                        <div className="media-lib-detail__stat">
                          <strong>{detail.copyCount}</strong>
                          <span>Copies</span>
                        </div>
                        <div className="media-lib-detail__stat">
                          <strong>{detail.downloadCount}</strong>
                          <span>Downloads</span>
                        </div>
                        <div className="media-lib-detail__stat">
                          <strong>{detail.copyLinkCount}</strong>
                          <span>Links</span>
                        </div>
                      </div>
                    </section>
                    {detail.sourceUrl ? (
                      <p className="media-lib-detail__source">
                        <a href={detail.sourceUrl} target="_blank" rel="noreferrer">
                          {detail.sourceUrl}
                        </a>
                      </p>
                    ) : null}
                  </div>
                )}

                {!editing ? (
                  <>
                    <section
                      className="media-lib-detail__reactions"
                      aria-labelledby="media-lib-reactions-heading"
                    >
                      <div className="media-lib-detail__section-head">
                        <span id="media-lib-reactions-heading" className="utility-app__label">
                          Reactions
                        </span>
                        <div className="media-lib-detail__reaction-actions">
                          {detail.currentUserReaction ? (
                            <span className="media-lib-detail__your-reaction">
                              <span aria-label={`Your reaction ${detail.currentUserReaction}`}>
                                {detail.currentUserReaction}
                              </span>
                              <button
                                type="button"
                                className="media-lib-detail__clear-reaction"
                                aria-label="Clear your reaction"
                                disabled={busy}
                                onClick={() => void onClearReaction()}
                              >
                                ×
                              </button>
                            </span>
                          ) : null}
                          <MediaLibraryReactionPicker
                            disabled={busy}
                            currentEmoji={detail.currentUserReaction}
                            onSelect={(emoji) => void onReact(emoji)}
                          />
                        </div>
                      </div>
                      {detail.topReactions?.length ? (
                        <div className="media-lib-detail__reaction-chips" role="list">
                          {detail.topReactions.map((row) => {
                            const mine = detail.currentUserReaction === row.emoji;
                            return (
                              <span
                                key={row.emoji}
                                role="listitem"
                                className={`media-lib-detail__reaction-chip${mine ? ' is-mine' : ''}`}
                                title={`${row.count} reaction${row.count === 1 ? '' : 's'}`}
                              >
                                <span aria-hidden="true">{row.emoji}</span> {row.count}
                              </span>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="utility-app__muted">No reactions yet</p>
                      )}
                    </section>

                    <div className="media-lib-detail__rating" role="group" aria-label="Rate this media">
                      <span className="utility-app__label">Your rating</span>
                      <div className="media-lib-detail__stars">
                        {[1, 2, 3, 4, 5].map((stars) => {
                          const filled = userRating >= stars;
                          return (
                            <button
                              key={stars}
                              type="button"
                              className={`media-lib-detail__star${filled ? ' is-filled' : ''}`}
                              aria-label={`Rate ${stars} star${stars === 1 ? '' : 's'}`}
                              aria-pressed={filled}
                              disabled={busy}
                              onClick={() => void onRate(stars)}
                            >
                              {filled ? '★' : '☆'}
                            </button>
                          );
                        })}
                        <span className="utility-app__muted">
                          {detail.ratingCount > 0
                            ? `Avg ${detail.averageRating?.toFixed(1) ?? '—'} (${detail.ratingCount})`
                            : 'No ratings yet'}
                        </span>
                      </div>
                    </div>
                  </>
                ) : null}
              </div>
            </div>

            {detail.canManage && !editing ? (
              <div className="media-lib-detail__admin" role="group" aria-label="Technical metadata">
                <div className="media-lib-detail__admin-head">
                  <p className="media-lib-detail__admin-title">Technical</p>
                  <div className="media-lib-detail__pills">
                    {detail.processingState ? (
                      <span className="media-lib-detail__pill media-lib-detail__pill--state">
                        {detail.processingState}
                      </span>
                    ) : null}
                    {detail.contentVersion != null ? (
                      <span className="media-lib-detail__pill">v{detail.contentVersion}</span>
                    ) : null}
                  </div>
                </div>
                <p className="utility-app__muted">
                  Uploaded by{' '}
                  <span title={detail.uploadedByUserId ?? undefined}>
                    {detail.uploadedByDisplayName?.trim() ||
                      detail.uploadedByUserId ||
                      '—'}
                  </span>
                </p>
                {detail.processingError ? (
                  <p className="utility-app__muted media-lib-detail__tech">{detail.processingError}</p>
                ) : null}
                {detail.sha256 ? (
                  <p className="utility-app__muted media-lib-detail__tech media-lib-detail__tech--hash">
                    SHA-256 {detail.sha256}
                  </p>
                ) : null}
                <div className="media-lib-detail__dates utility-app__muted">
                  <p>Created {new Date(detail.createdUtc).toLocaleString()}</p>
                  <p>Updated {new Date(detail.updatedUtc).toLocaleString()}</p>
                </div>
              </div>
            ) : null}

            <div className="media-lib-detail__actions">
              <Button text="Close" stylingMode="outlined" disabled={busy} onClick={onClose} />
              {detail.canManage ? (
                editing ? (
                  <>
                    <Button
                      text="Cancel"
                      stylingMode="outlined"
                      disabled={busy}
                      onClick={() => setEditing(false)}
                    />
                    <Button
                      text={busy ? 'Saving…' : 'Save'}
                      type="success"
                      stylingMode="contained"
                      disabled={busy}
                      onClick={() => void onSave()}
                    />
                  </>
                ) : (
                  <>
                    <Button
                      text="Edit metadata"
                      type="default"
                      disabled={busy}
                      onClick={() => setEditing(true)}
                    />
                    {detail.kind !== 'UserUpload' ? (
                      <>
                        <Button
                          text="Replace file"
                          stylingMode="outlined"
                          disabled={busy}
                          onClick={() => replaceInputRef.current?.click()}
                        />
                        <input
                          ref={replaceInputRef}
                          type="file"
                          hidden
                          accept={
                            detail.kind === 'ManagedGif'
                              ? 'image/gif,.gif'
                              : 'image/jpeg,image/png,image/webp,image/avif,.jpg,.jpeg,.png,.webp,.avif'
                          }
                          onChange={(e) => {
                            const file = e.target.files?.[0] ?? null;
                            e.currentTarget.value = '';
                            void onReplace(file);
                          }}
                        />
                      </>
                    ) : null}
                    <Button
                      text="Delete"
                      icon="trash"
                      type="danger"
                      stylingMode="contained"
                      disabled={busy}
                      onClick={() => void onDelete()}
                    />
                  </>
                )
              ) : null}
            </div>
          </>
        )}
      </div>
    </Popup>
  );
}
